import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  // step: "credentials" | "otp"
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // OTP state
  const [challengeId, setChallengeId] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Auto-focus first OTP box when step changes
  useEffect(() => {
    if (step === "otp") {
      inputsRef.current[0]?.focus();
    }
  }, [step]);

  const onSubmitCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Sign-in failed.");
      return;
    }
    if (res.otpRequired) {
      setChallengeId(res.challengeId);
      setEmailMasked(res.emailMasked);
      setResendIn(res.resendAfter || 30);
      setCode(["", "", "", "", "", ""]);
      setStep("otp");
      toast.success("Verification code sent.");
      return;
    }
    // Backwards-compatible path (no 2FA configured)
    toast.success("Welcome back.");
    navigate(next);
  };

  const onChangeDigit = (idx, val) => {
    const clean = val.replace(/\D/g, "").slice(0, 1);
    setCode((c) => {
      const copy = [...c];
      copy[idx] = clean;
      return copy;
    });
    if (clean && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDownDigit = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const onPasteCode = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, 6);
    if (digits.length === 0) return;
    e.preventDefault();
    const next6 = ["", "", "", "", "", ""];
    for (let i = 0; i < digits.length; i += 1) next6[i] = digits[i];
    setCode(next6);
    const lastIdx = Math.min(digits.length, 6) - 1;
    inputsRef.current[lastIdx]?.focus();
  };

  const onSubmitOtp = async (e) => {
    e?.preventDefault?.();
    const full = code.join("");
    if (full.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setError("");
    setBusy(true);
    const res = await verifyOtp(challengeId, full);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Verification failed.");
      setCode(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
      return;
    }
    toast.success("Verified — welcome back.");
    navigate(next);
  };

  const onResend = async () => {
    if (resendIn > 0) return;
    setError("");
    setBusy(true);
    const res = await resendOtp(challengeId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Could not re-send.");
      return;
    }
    setResendIn(res.resendAfter || 30);
    setCode(["", "", "", "", "", ""]);
    inputsRef.current[0]?.focus();
    toast.success("New code sent.");
  };

  const backToCredentials = () => {
    setStep("credentials");
    setError("");
    setCode(["", "", "", "", "", ""]);
    setChallengeId("");
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center px-6 pt-24 pb-12">
      <div className="w-full max-w-md">
        {step === "credentials" && (
          <>
            <p className="eyebrow mb-4">Partner · Member</p>
            <h1 className="font-display text-5xl md:text-6xl mb-3">Sign In</h1>
            <p className="text-sm mb-10" style={{ color: "var(--ink-muted)" }}>
              Welcome back to the archive. Sign-in is protected by 2-step verification.
            </p>
            <form onSubmit={onSubmitCredentials} className="space-y-6">
              <div>
                <label className="eyebrow block mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-line"
                  data-testid="login-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="eyebrow block mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-line"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <p className="text-sm" style={{ color: "#E08585" }} data-testid="login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="btn-brass w-full justify-center"
              >
                {busy ? "Signing in…" : "Continue"}
              </button>
            </form>
            <p className="mt-8 text-sm text-center" style={{ color: "var(--ink-muted)" }}>
              New here?{" "}
              <Link to="/register" className="brass-link uppercase tracking-[0.2em] text-xs">
                Create an account
              </Link>
            </p>
          </>
        )}

        {step === "otp" && (
          <div data-testid="login-otp-step">
            <button
              type="button"
              onClick={backToCredentials}
              className="text-xs uppercase tracking-[0.2em] mb-6 inline-flex items-center gap-2"
              style={{ color: "var(--ink-muted)" }}
              data-testid="login-otp-back"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <div className="mb-6 flex items-center gap-3">
              <ShieldCheck size={22} style={{ color: "var(--brass)" }} />
              <p className="eyebrow">Two-Step Verification</p>
            </div>
            <h1 className="font-display text-4xl md:text-5xl mb-4 leading-[1.05]">
              Enter the 6-digit code.
            </h1>
            <p className="text-sm mb-8 leading-relaxed" style={{ color: "var(--ink-muted)" }}>
              We sent a verification code to{" "}
              <span style={{ color: "var(--ink)" }}>{emailMasked}</span>. It expires in 10 minutes.
            </p>
            <form onSubmit={onSubmitOtp} className="space-y-6">
              <div
                className="flex gap-2 justify-between"
                data-testid="login-otp-inputs"
                onPaste={onPasteCode}
              >
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => {
                      inputsRef.current[idx] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => onChangeDigit(idx, e.target.value)}
                    onKeyDown={(e) => onKeyDownDigit(idx, e)}
                    className="w-12 h-14 text-center font-display text-2xl bg-transparent outline-none transition-colors"
                    style={{
                      border: "1px solid var(--hairline-strong)",
                      color: "var(--ink)",
                      borderColor: digit ? "var(--brass)" : "var(--hairline-strong)",
                    }}
                    data-testid={`login-otp-digit-${idx}`}
                    autoComplete={idx === 0 ? "one-time-code" : "off"}
                  />
                ))}
              </div>
              {error && (
                <p className="text-sm" style={{ color: "#E08585" }} data-testid="login-otp-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={busy || code.join("").length !== 6}
                className="btn-brass w-full justify-center"
                data-testid="login-otp-submit"
              >
                {busy ? "Verifying…" : "Verify & Sign In"}
              </button>
            </form>

            <div className="mt-8 text-center text-sm" style={{ color: "var(--ink-muted)" }}>
              {resendIn > 0 ? (
                <span data-testid="login-otp-resend-timer">Resend available in {resendIn}s</span>
              ) : (
                <button
                  type="button"
                  onClick={onResend}
                  disabled={busy}
                  className="brass-link uppercase tracking-[0.2em] text-xs"
                  data-testid="login-otp-resend"
                >
                  Resend Code
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
